import { useState, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useOCR, useExpenses, useCategories, useLoads, useRevenueCategories } from '../hooks/useApi'
import { fileToBase64, formatCurrency, getCurrentDateString } from '../lib/utils'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Switch } from '../components/ui/switch'
import { toast } from 'sonner'
import { Camera, Upload, Loader2, Check, Edit2, X, Truck, Receipt, Mail, FileText, Plus, DollarSign, Trash2 } from 'lucide-react'
import { useEffect } from 'react'
import { analytics } from '../lib/analytics'

// Create empty line item
const createEmptyLineItem = () => ({
  category: 'linehaul',
  amount: '',
  is_reimbursement: false,
  description: ''
})

export default function Scanner() {
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('type') === 'load' ? 'load' : 'receipt')
  const [inputMode, setInputMode] = useState(searchParams.get('email') === 'true' ? 'email' : 'image')
  const [emailText, setEmailText] = useState('')
  const [imagePreview, setImagePreview] = useState(null)
  const [imageBase64, setImageBase64] = useState(null)
  const [ocrData, setOcrData] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [categories, setCategories] = useState([])
  const [revenueCategories, setRevenueCategories] = useState([])
  
  // Receipt form state
  const [receiptForm, setReceiptForm] = useState({
    date: getCurrentDateString(),
    amount: '',
    merchant: '',
    category_id: 'fuel',
    notes: ''
  })
  
  // Load form state with line_items support
  const [loadForm, setLoadForm] = useState({
    pickup_date: getCurrentDateString(),
    delivery_date: getCurrentDateString(),
    pickup_location: '',
    delivery_location: '',
    miles: '',
    broker: '',
    load_number: '',
    notes: '',
    line_items: [createEmptyLineItem()]
  })
  
  // Calculate total revenue from line items
  const loadTotalRevenue = loadForm.line_items.reduce((sum, item) => {
    const amount = parseFloat(item.amount) || 0
    return sum + amount
  }, 0)
  
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  const navigate = useNavigate()
  
  const { processReceipt, processLoad, processEmailText, loading: ocrLoading } = useOCR()
  const { createExpense, loading: expenseLoading } = useExpenses()
  const { createLoad, loading: loadLoading } = useLoads()
  const { fetchCategories } = useCategories()
  const { fetchRevenueCategories } = useRevenueCategories()

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      const [cats, revCats] = await Promise.all([
        fetchCategories(),
        fetchRevenueCategories()
      ])
      setCategories(cats)
      setRevenueCategories(revCats)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  const handleFileSelect = useCallback(async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }
    
    // Create preview
    const previewUrl = URL.createObjectURL(file)
    setImagePreview(previewUrl)
    
    // Convert to base64
    try {
      const base64 = await fileToBase64(file)
      setImageBase64(base64)
      setOcrData(null)
      setIsEditing(false)
      
      // Auto-process OCR
      await processOCR(base64)
    } catch (err) {
      toast.error('Failed to process image')
    }
  }, [activeTab])

  const processOCR = async (base64) => {
    // Track scan attempt
    analytics.receiptScanAttempt(inputMode === 'email' ? 'email' : 'upload')
    
    try {
      let result
      if (activeTab === 'receipt') {
        result = await processReceipt(base64)
      } else {
        result = await processLoad(base64)
      }
      
      if (result.success) {
        setOcrData(result.data)
        
        // Populate form with OCR data
        if (activeTab === 'receipt') {
          setReceiptForm(prev => ({
            ...prev,
            date: result.data.date || prev.date,
            amount: result.data.amount?.toString() || '',
            merchant: result.data.merchant || '',
            category_id: result.data.suggested_category || 'fuel'
          }))
        } else {
          // Handle load with line_items
          let lineItems = []
          
          if (result.data.line_items && result.data.line_items.length > 0) {
            // OCR returned line items
            lineItems = result.data.line_items.map(item => ({
              category: item.category || 'linehaul',
              amount: item.amount?.toString() || '',
              is_reimbursement: item.is_reimbursement || false,
              description: item.description || ''
            }))
          } else if (result.data.rate || result.data.total_rate) {
            // Legacy single rate - convert to line item
            lineItems = [{
              category: 'linehaul',
              amount: (result.data.rate || result.data.total_rate)?.toString() || '',
              is_reimbursement: false,
              description: 'Total Rate'
            }]
          } else {
            lineItems = [createEmptyLineItem()]
          }
          
          setLoadForm(prev => ({
            ...prev,
            pickup_date: result.data.pickup_date || prev.pickup_date,
            delivery_date: result.data.delivery_date || prev.delivery_date,
            pickup_location: result.data.pickup_location || '',
            delivery_location: result.data.delivery_location || '',
            miles: result.data.miles?.toString() || '',
            broker: result.data.broker || '',
            load_number: result.data.load_number || '',
            line_items: lineItems
          }))
        }
        
        toast.success('Document scanned successfully!')
        setIsEditing(true)
      } else {
        toast.error('Could not extract data. Please enter manually.')
        setIsEditing(true)
      }
    } catch (err) {
      toast.error('OCR failed. Please enter data manually.')
      setIsEditing(true)
    }
  }

  const handleSaveReceipt = async () => {
    if (!receiptForm.amount || !receiptForm.merchant) {
      toast.error('Please fill in amount and merchant')
      return
    }
    
    try {
      await createExpense({
        ...receiptForm,
        amount: parseFloat(receiptForm.amount),
        receipt_image: imageBase64
      })
      analytics.receiptSaved(receiptForm.category_id, parseFloat(receiptForm.amount))
      toast.success('Expense saved!')
      navigate('/expenses')
    } catch (err) {
      toast.error('Failed to save expense')
    }
  }

  const handleSaveLoad = async () => {
    if (!loadForm.pickup_location || !loadForm.delivery_location) {
      toast.error('Please fill in pickup and delivery locations')
      return
    }
    
    // Validate line items - at least one with amount
    const validLineItems = loadForm.line_items.filter(item => item.amount && parseFloat(item.amount) > 0)
    if (validLineItems.length === 0) {
      toast.error('Please add at least one line item with an amount')
      return
    }
    
    try {
      await createLoad({
        pickup_date: loadForm.pickup_date,
        delivery_date: loadForm.delivery_date,
        pickup_location: loadForm.pickup_location,
        delivery_location: loadForm.delivery_location,
        miles: loadForm.miles ? parseFloat(loadForm.miles) : null,
        broker: loadForm.broker,
        load_number: loadForm.load_number,
        notes: loadForm.notes,
        line_items: validLineItems.map(item => ({
          category: item.category,
          amount: parseFloat(item.amount),
          is_reimbursement: item.is_reimbursement || false,
          description: item.description || ''
        })),
        document_image: imageBase64
      })
      analytics.loadSaved(loadTotalRevenue)
      toast.success('Load saved!')
      navigate('/loads')
    } catch (err) {
      toast.error('Failed to save load')
    }
  }

  const resetScanner = () => {
    setImagePreview(null)
    setImageBase64(null)
    setEmailText('')
    setInputMode('image')
    setOcrData(null)
    setIsEditing(false)
    setReceiptForm({
      date: getCurrentDateString(),
      amount: '',
      merchant: '',
      category_id: 'fuel',
      notes: ''
    })
    setLoadForm({
      pickup_date: getCurrentDateString(),
      delivery_date: getCurrentDateString(),
      pickup_location: '',
      delivery_location: '',
      miles: '',
      broker: '',
      load_number: '',
      notes: '',
      line_items: [createEmptyLineItem()]
    })
  }
  
  // Line item management for loads
  const addLineItem = () => {
    setLoadForm(f => ({
      ...f,
      line_items: [...f.line_items, createEmptyLineItem()]
    }))
  }
  
  const removeLineItem = (index) => {
    if (loadForm.line_items.length > 1) {
      setLoadForm(f => ({
        ...f,
        line_items: f.line_items.filter((_, i) => i !== index)
      }))
    }
  }
  
  const updateLineItem = (index, field, value) => {
    setLoadForm(f => ({
      ...f,
      line_items: f.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }))
  }

  const processEmailContent = async () => {
    if (!emailText.trim()) {
      toast.error('Please paste email content first')
      return
    }
    
    try {
      const result = await processEmailText(emailText, activeTab)
      
      if (result.success) {
        setOcrData(result.data)
        
        if (activeTab === 'receipt') {
          setReceiptForm(prev => ({
            ...prev,
            date: result.data.date || prev.date,
            amount: result.data.amount?.toString() || '',
            merchant: result.data.merchant || '',
            category_id: result.data.suggested_category || 'fuel'
          }))
        } else {
          // Handle load with line_items
          let lineItems = []
          
          if (result.data.line_items && result.data.line_items.length > 0) {
            // OCR returned line items
            lineItems = result.data.line_items.map(item => ({
              category: item.category || 'linehaul',
              amount: item.amount?.toString() || '',
              is_reimbursement: item.is_reimbursement || false,
              description: item.description || ''
            }))
          } else if (result.data.rate || result.data.total_rate) {
            // Legacy single rate - convert to line item
            lineItems = [{
              category: 'linehaul',
              amount: (result.data.rate || result.data.total_rate)?.toString() || '',
              is_reimbursement: false,
              description: 'Total Rate'
            }]
          } else {
            lineItems = [createEmptyLineItem()]
          }
          
          setLoadForm(prev => ({
            ...prev,
            pickup_date: result.data.pickup_date || prev.pickup_date,
            delivery_date: result.data.delivery_date || prev.delivery_date,
            pickup_location: result.data.pickup_location || '',
            delivery_location: result.data.delivery_location || '',
            miles: result.data.miles?.toString() || '',
            broker: result.data.broker || '',
            load_number: result.data.load_number || '',
            line_items: lineItems
          }))
        }
        
        toast.success('Email content processed!')
        setIsEditing(true)
      } else {
        toast.error('Could not extract data. Please enter manually.')
        setIsEditing(true)
      }
    } catch (err) {
      toast.error('Processing failed. Please enter data manually.')
      setIsEditing(true)
    }
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scanner-page">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
          SCAN DOCUMENT
        </h1>
        <p className="text-muted-foreground">
          Upload a receipt or load document for quick entry
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); resetScanner(); }}>
        <TabsList className="grid w-full grid-cols-2 h-12">
          <TabsTrigger value="receipt" className="h-10 text-base" data-testid="tab-receipt">
            <Receipt className="w-4 h-4 mr-2" />
            Receipt
          </TabsTrigger>
          <TabsTrigger value="load" className="h-10 text-base" data-testid="tab-load">
            <Truck className="w-4 h-4 mr-2" />
            Load Doc
          </TabsTrigger>
        </TabsList>

        <TabsContent value="receipt" className="mt-4">
          {!imagePreview && !isEditing ? (
            <UploadArea
              onFileSelect={handleFileSelect}
              fileInputRef={fileInputRef}
              cameraInputRef={cameraInputRef}
              label="Snap or upload a receipt"
              inputMode={inputMode}
              setInputMode={setInputMode}
              emailText={emailText}
              setEmailText={setEmailText}
              onProcessEmail={processEmailContent}
              ocrLoading={ocrLoading}
              documentType="receipt"
            />
          ) : (
            <div className="space-y-4">
              <ImagePreview 
                src={imagePreview} 
                onReset={resetScanner}
                loading={ocrLoading}
              />
              
              {isEditing && (
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-['Barlow_Condensed']">
                      EXPENSE DETAILS
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={receiptForm.date}
                          onChange={(e) => setReceiptForm(f => ({ ...f, date: e.target.value }))}
                          className="h-12"
                          data-testid="receipt-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          value={receiptForm.amount}
                          onChange={(e) => setReceiptForm(f => ({ ...f, amount: e.target.value }))}
                          className="h-12 text-lg font-semibold"
                          data-testid="receipt-amount"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Merchant</Label>
                      <Input
                        placeholder="e.g., Pilot Flying J"
                        value={receiptForm.merchant}
                        onChange={(e) => setReceiptForm(f => ({ ...f, merchant: e.target.value }))}
                        className="h-12"
                        data-testid="receipt-merchant"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select
                        value={receiptForm.category_id}
                        onValueChange={(v) => setReceiptForm(f => ({ ...f, category_id: v }))}
                      >
                        <SelectTrigger className="h-12" data-testid="receipt-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Notes (optional)</Label>
                      <Input
                        placeholder="Add any notes..."
                        value={receiptForm.notes}
                        onChange={(e) => setReceiptForm(f => ({ ...f, notes: e.target.value }))}
                        className="h-12"
                        data-testid="receipt-notes"
                      />
                    </div>
                    
                    <Button
                      onClick={handleSaveReceipt}
                      disabled={expenseLoading}
                      className="w-full h-12 rounded-full font-semibold"
                      data-testid="save-receipt-btn"
                    >
                      {expenseLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Save Expense
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="load" className="mt-4">
          {!imagePreview && !isEditing ? (
            <UploadArea
              onFileSelect={handleFileSelect}
              fileInputRef={fileInputRef}
              cameraInputRef={cameraInputRef}
              label="Snap or upload load document"
              inputMode={inputMode}
              setInputMode={setInputMode}
              emailText={emailText}
              setEmailText={setEmailText}
              onProcessEmail={processEmailContent}
              ocrLoading={ocrLoading}
              documentType="load"
            />
          ) : (
            <div className="space-y-4">
              <ImagePreview 
                src={imagePreview} 
                onReset={resetScanner}
                loading={ocrLoading}
              />
              
              {isEditing && (
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-['Barlow_Condensed']">
                      LOAD DETAILS
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Pickup Date</Label>
                        <Input
                          type="date"
                          value={loadForm.pickup_date}
                          onChange={(e) => setLoadForm(f => ({ ...f, pickup_date: e.target.value }))}
                          className="h-12"
                          data-testid="load-pickup-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Delivery Date</Label>
                        <Input
                          type="date"
                          value={loadForm.delivery_date}
                          onChange={(e) => setLoadForm(f => ({ ...f, delivery_date: e.target.value }))}
                          className="h-12"
                          data-testid="load-delivery-date"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Pickup Location</Label>
                      <Input
                        placeholder="e.g., Dallas, TX"
                        value={loadForm.pickup_location}
                        onChange={(e) => setLoadForm(f => ({ ...f, pickup_location: e.target.value }))}
                        className="h-12"
                        data-testid="load-pickup-location"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Delivery Location</Label>
                      <Input
                        placeholder="e.g., Chicago, IL"
                        value={loadForm.delivery_location}
                        onChange={(e) => setLoadForm(f => ({ ...f, delivery_location: e.target.value }))}
                        className="h-12"
                        data-testid="load-delivery-location"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Miles</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={loadForm.miles}
                          onChange={(e) => setLoadForm(f => ({ ...f, miles: e.target.value }))}
                          className="h-12"
                          data-testid="load-miles"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Load #</Label>
                        <Input
                          placeholder="Reference #"
                          value={loadForm.load_number}
                          onChange={(e) => setLoadForm(f => ({ ...f, load_number: e.target.value }))}
                          className="h-12"
                          data-testid="load-number"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Broker</Label>
                      <Input
                        placeholder="Broker name"
                        value={loadForm.broker}
                        onChange={(e) => setLoadForm(f => ({ ...f, broker: e.target.value }))}
                        className="h-12"
                        data-testid="load-broker"
                      />
                    </div>
                    
                    {/* Revenue Line Items Section */}
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-green-500" />
                          Revenue Line Items
                        </Label>
                        <span className="text-sm text-muted-foreground">
                          {loadForm.line_items.length} item{loadForm.line_items.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {/* Line Items List */}
                      <div className="space-y-3">
                        {loadForm.line_items.map((item, index) => (
                          <div key={index} className="p-3 bg-secondary/30 rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-muted-foreground">
                                Line {index + 1} {item.description && `- ${item.description}`}
                              </span>
                              {loadForm.line_items.length > 1 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeLineItem(index)}
                                  className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Remove
                                </Button>
                              )}
                            </div>
                            
                            {/* Category Dropdown */}
                            <Select
                              value={item.category}
                              onValueChange={(value) => updateLineItem(index, 'category', value)}
                            >
                              <SelectTrigger className="h-11">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                              {revenueCategories.map((cat) => (
                                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            {/* Amount Input */}
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-semibold text-muted-foreground">$</span>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={item.amount}
                                onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                                className="h-11 text-lg font-semibold flex-1"
                              />
                            </div>
                            
                            {/* Reimbursement Toggle */}
                            <div className="flex items-center justify-between py-2 px-3 bg-background/50 rounded-md">
                              <span className="text-sm">Reimbursement / Non-income</span>
                              <Switch
                                checked={item.is_reimbursement}
                                onCheckedChange={(checked) => updateLineItem(index, 'is_reimbursement', checked)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Add Line Item Button */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addLineItem}
                        className="w-full h-11 border-dashed border-2"
                      >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Another Line Item
                      </Button>
                      
                      {/* Running Total */}
                      <div className="flex items-center justify-between p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                        <span className="font-semibold text-green-700 dark:text-green-400">Total Revenue</span>
                        <span className="text-xl font-bold text-green-700 dark:text-green-400">
                          {formatCurrency(loadTotalRevenue)}
                        </span>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleSaveLoad}
                      disabled={loadLoading}
                      className="w-full h-12 rounded-full font-semibold"
                      data-testid="save-load-btn"
                    >
                      {loadLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      Save Load
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function UploadArea({ onFileSelect, fileInputRef, cameraInputRef, label, inputMode, setInputMode, emailText, setEmailText, onProcessEmail, ocrLoading, documentType }) {
  return (
    <div className="space-y-4">
      {/* Mode Selector */}
      <div className="grid grid-cols-2 gap-2 p-1 bg-secondary rounded-lg">
        <Button
          variant={inputMode === 'image' ? 'default' : 'ghost'}
          className="h-10"
          onClick={() => setInputMode('image')}
          data-testid="mode-image"
        >
          <Camera className="w-4 h-4 mr-2" />
          Image
        </Button>
        <Button
          variant={inputMode === 'email' ? 'default' : 'ghost'}
          className="h-10"
          onClick={() => setInputMode('email')}
          data-testid="mode-email"
        >
          <Mail className="w-4 h-4 mr-2" />
          From Email
        </Button>
      </div>

      {inputMode === 'image' ? (
        <>
          <div 
            className="dropzone flex flex-col items-center justify-center min-h-[250px]"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">{label}</p>
            <p className="text-sm text-muted-foreground">
              Tap to browse or use camera
            </p>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onFileSelect}
              className="hidden"
              data-testid="file-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="h-14 rounded-xl"
              onClick={() => cameraInputRef.current?.click()}
              data-testid="camera-btn"
            >
              <Camera className="w-5 h-5 mr-2" />
              Camera
            </Button>
            <Button
              variant="outline"
              className="h-14 rounded-xl"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-btn"
            >
              <Upload className="w-5 h-5 mr-2" />
              Upload
            </Button>
            
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onFileSelect}
              className="hidden"
            />
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="w-5 h-5" />
              <span className="text-sm">
                {documentType === 'load' 
                  ? 'Paste rate confirmation or load offer email content below'
                  : 'Paste receipt email content below'}
              </span>
            </div>
            
            <Textarea
              placeholder={documentType === 'load' 
                ? "Paste the email content here...\n\nExample:\nRate Confirmation\nLoad #12345\nPickup: Dallas, TX - 01/15/2025\nDelivery: Chicago, IL - 01/17/2025\nRate: $2,500\nMiles: 920\nBroker: ABC Logistics"
                : "Paste the email content here...\n\nExample:\nReceipt from Pilot Flying J\nDate: 01/15/2025\nFuel: $450.00\nDEF: $25.00\nTotal: $475.00"}
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              className="min-h-[200px] font-mono text-sm"
              data-testid="email-text-input"
            />
            
            <Button
              onClick={onProcessEmail}
              disabled={ocrLoading || !emailText.trim()}
              className="w-full h-12 rounded-full font-semibold"
              data-testid="process-email-btn"
            >
              {ocrLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Extract Data from Email
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ImagePreview({ src, onReset, loading }) {
  return (
    <Card className="overflow-hidden">
      <div className="relative">
        <img 
          src={src} 
          alt="Document preview" 
          className="w-full h-auto max-h-[400px] object-contain bg-black/5"
        />
        
        {loading && (
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm font-medium">Scanning document...</p>
            </div>
          </div>
        )}
        
        <Button
          variant="secondary"
          size="icon"
          className="absolute top-2 right-2"
          onClick={onReset}
          data-testid="reset-scanner-btn"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  )
}
